import client from 'app/client';
import { AddressDto, OperationResponse } from 'api/actor';
import { MandantSelection } from 'back-office/shared/components/mandants/mandantTypes';

/**
 * Création complète d'un MIA (Mandataire d'Intermédiaire en Assurance) :
 * user ABP (rôle MIA) + PM nature MIA + ligne Courtier + liaisons vers ses
 * courtiers mandants avec, pour chacun, les tunnels accordés VIA lui
 * (granularité paire mandant×tunnel). Les habilitations dérivées sont posées
 * par le backend dans la même transaction.
 */
export interface CreateMiaCommand {
  companyName: string;
  address?: AddressDto;
  /** Téléphone de l'entreprise (standard), facultatif. */
  phone?: string | null;
  /** Gérant juridique : civilité et mobile, requis. */
  gerant: { civiliteId: string; phone: string };
  registrationNumber: string;
  capital: number;
  rcsCity: string;
  rcsNumber: string;
  oriasNumber: string;
  rcp?: string | null;
  complaintEmail: string;
  mandants: MandantSelection[];
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  plateformeIds: string[];
}

export interface MiaMandantDto {
  courtierId: string;
  label?: string | null;
  /** Tunnels accordés via ce mandant (sélections actives de la liaison). */
  tunnelIds: string[];
}

export interface MiaListItemDto {
  id: string;
  courtierId: string;
  label: string;
  registrationNumber?: string;
  oriasNumber?: string | null;
  email?: string | null;
  phone?: string;
  creationDate: string;
  mandants: MiaMandantDto[];
  /** OUs de rattachement du MIA (filtrage des mandants proposables). */
  plateformeIds: string[];
}

export interface SearchMiasResponse {
  items: MiaListItemDto[];
  totalCount: number;
}

export interface SearchMiasCommand {
  searchString?: string;
  pageNumber: number;
  pageSize: number;
}

export interface UpdateMiaMandantsCommand {
  mandants: MandantSelection[];
}

export interface MiaTunnelsAutorisesResponse {
  plateformeId: string;
  miaCourtierId: string;
  tunnelIds: string[];
}

export async function createMia(command: CreateMiaCommand): Promise<OperationResponse> {
  const { data } = await client.post<OperationResponse>('/backoffice/mias', command);
  return data;
}

export async function searchMias(command: SearchMiasCommand): Promise<SearchMiasResponse> {
  const { data } = await client.post<SearchMiasResponse>('/backoffice/mias/liste', command);
  return data;
}

/**
 * État cible des mandants du MIA, tunnels par mandant inclus. Le backend
 * resynchronise les habilitations dérivées (pose + cascade orphelines) —
 * le bilan est remonté dans response.notification.
 */
export async function updateMiaMandants(
  miaCourtierId: string,
  command: UpdateMiaMandantsCommand,
): Promise<OperationResponse> {
  const { data } = await client.put<OperationResponse>(
    `/backoffice/mias/${miaCourtierId}/mandants`,
    command,
  );
  return data;
}

/**
 * Tunnels proposables pour habiliter un MIA dans une OU
 * (union des habilitations actives de ses courtiers mandants).
 */
export async function getTunnelsAutorisesPourMia(
  plateformeId: string,
  miaCourtierId: string,
): Promise<MiaTunnelsAutorisesResponse> {
  const { data } = await client.get<MiaTunnelsAutorisesResponse>(
    `/backoffice/plateformes/${plateformeId}/habilitations-tunnels/mias/${miaCourtierId}/tunnels-autorises`,
  );
  return data;
}
